import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/lib/supabaseClient";
import { SiShopify } from "react-icons/si";
import { Check, RefreshCcw, Globe } from "lucide-react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { LoadingButtonContent } from "@/components/ui/loading-button-content";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
    AlertDialogCancel,
    AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

const shopifyFormSchema = z.object({
    domain: z.string().min(1, "Store domain is required"),
});

function Integrations() {
    const [isConnected, setIsConnected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [storeDomain, setStoreDomain] = useState("");
    const [isPixelInstalled, setIsPixelInstalled] = useState(false);
    const [globaleGuid, setGlobaleGuid] = useState("");
    const { toast } = useToast();
    const { session, isDemoMode } = useAuth();
    const form = useForm<z.infer<typeof shopifyFormSchema>>({
        resolver: zodResolver(shopifyFormSchema),
        defaultValues: {
            domain: "",
        },
    });

    useEffect(() => {
        checkConnectionStatus();
    }, []);

    const checkConnectionStatus = async () => {
        try {
            // If in demo mode, show a demo store connection
            if (isDemoMode) {
                setIsConnected(true);
                setStoreDomain("optifi-demo.myshopify.com");
                setInitialLoading(false);
                setIsPixelInstalled(true);
                return;
            }

            const { data, error } = await supabase
                .from("shops")
                .select("access_token, shop, globale_guid")
                .single();

            setIsConnected(!!data?.access_token);
            if (data?.shop) {
                setStoreDomain(data.shop);
            }
            if (data?.globale_guid) {
                setGlobaleGuid(data.globale_guid);
            }
        } finally {
            setInitialLoading(false);
        }
    };

    const handleDisconnect = async () => {
        setLoading(true);
        try {
            const { error } = await supabase
                .from("shops")
                .delete()
                .eq("shop", storeDomain);

            if (error) throw error;

            setIsConnected(false);
            setStoreDomain("");
            toast({
                title: "Store disconnected successfully",
            });
        } catch (error: any) {
            toast({
                title: "Failed to disconnect store",
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleShopifyIntegrationConnection = async (
        values: z.infer<typeof shopifyFormSchema>
    ) => {
        setLoading(true);

        // Add Shop URL to the database
        try {
            const { data, error } = await supabase.from("shops").upsert({
                shop: values.domain,
                platform: "shopify",
                user_id: session?.user.id,
            });

            if (error) throw error;
        } catch (error: any) {
            toast({
                title: "Failed to save store information",
                description: error.message,
                variant: "destructive",
            });
            return;
        }

        try {
            const response = await fetch(
                `${
                    import.meta.env.VITE_OPTIFI_API_URL
                }/integrations/shopify/install`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        domain: values.domain,
                        redirect_url: `${
                            import.meta.env.VITE_OPTIFI_API_URL
                        }/integrations/shopify/callback`,
                    }),
                }
            );

            const data = await response.json();
            window.location.href = data.url;
        } catch (error) {
            toast({
                title: "Failed to connect to Shopify",
                description: "Please check your store domain and try again.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEnableApp = () => {
        const shopifyAdminUrl = `https://${storeDomain}/admin/themes/current/editor?context=apps`;
        window.open(shopifyAdminUrl, "_blank");
    };

    const handleGlobaleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);

        try {
            const formData = new FormData(e.currentTarget);
            const guid = formData.get("globaleGuid");

            setGlobaleGuid(guid);

            // Update the shop record with the Global-e GUID
            const { error } = await supabase
                .from("shops")
                .update({ globale_guid: guid })
                .eq("user_id", session?.user.id);

            if (error) throw error;

            toast({
                title: "Global-e GUID saved successfully",
            });
        } catch (error: any) {
            toast({
                title: "Failed to save Global-e GUID",
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const maskGuid = (guid: string) => {
        if (!guid) return "";
        if (guid.length <= 4) return guid;
        return "*".repeat(guid.length - 4) + guid.slice(-4);
    };

    return (
        <div className="space-y-6">

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-4">
                        <SiShopify className="h-8 w-8" />
                        <div>
                            <CardTitle>Shopify Integration</CardTitle>
                            <CardDescription>
                                Connect your Shopify store to start optimizing
                                your products
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {initialLoading ? (
                        <div className="h-[100px] flex items-center justify-center">
                            <LoadingButtonContent loading={true}>
                                Loading
                            </LoadingButtonContent>
                        </div>
                    ) : isConnected ? (
                        <div className="space-y-6">
                            <div>
                                <div className="flex items-center gap-2 text-sm text-green-600">
                                    <Check className="h-5 w-5" />
                                    <span>Store connected successfully</span>
                                </div>
                                <div className="mt-2 text-sm text-muted-foreground space-y-1">
                                    <div>
                                        <span className="font-medium">
                                            Connected Store:
                                        </span>{" "}
                                        {storeDomain}
                                    </div>
                                </div>
                            </div>
                            {!isPixelInstalled && (
                                <div className="rounded-lg border p-4 bg-muted/50">
                                    <h4 className="font-medium mb-2">
                                        Enable App in Your Theme
                                    </h4>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        To complete the setup, you need to
                                        enable the app in your store's theme.
                                        Click below to open the theme editor and
                                        enable the app's features.
                                    </p>
                                    <Button onClick={handleEnableApp}>
                                        Enable App in Theme Editor
                                    </Button>
                                </div>
                            )}
                            {isPixelInstalled && (
                                <div className="rounded-lg border p-4 bg-muted/50">
                                    <h4 className="font-medium mb-2">
                                        Optifi Pixel Detected
                                    </h4>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        The pixel appears to have been
                                        successfully installed on your store.
                                        You can now start optimizing your
                                        products.
                                    </p>
                                </div>
                            )}

                            <div className="border-t pt-4">
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="destructive">
                                            Disconnect Store
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>
                                                Disconnect Store
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Are you sure you want to
                                                disconnect {storeDomain}? This
                                                will stop all product
                                                optimizations.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>
                                                Cancel
                                            </AlertDialogCancel>
                                            <Button
                                                variant="destructive"
                                                onClick={handleDisconnect}
                                                disabled={loading}
                                            >
                                                <LoadingButtonContent
                                                    loading={loading}
                                                >
                                                    Disconnect
                                                </LoadingButtonContent>
                                            </Button>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                                <Button
                                    variant="outline"
                                    className="ml-2"
                                    onClick={() =>
                                        handleShopifyIntegrationConnection({
                                            domain: storeDomain,
                                        })
                                    }
                                >
                                    Re-authorize
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <Form {...form}>
                            <form
                                onSubmit={form.handleSubmit(
                                    handleShopifyIntegrationConnection
                                )}
                                className="space-y-4"
                            >
                                <FormField
                                    control={form.control}
                                    name="domain"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Store Domain</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="your-store.myshopify.com"
                                                    {...field}
                                                    autoComplete="off"
                                                />
                                            </FormControl>
                                            <FormDescription>
                                                Enter your Shopify store domain
                                                to connect
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <Button
                                    type="submit"
                                    className="w-[120px]"
                                    disabled={loading}
                                >
                                    <LoadingButtonContent loading={loading}>
                                        Connect
                                    </LoadingButtonContent>
                                </Button>
                            </form>
                        </Form>
                    )}
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-4">
                        <Globe className="h-8 w-8" />
                        <div>
                            <CardTitle>Global-e Integration</CardTitle>
                            <CardDescription>
                                If you use Global-e to manage your international
                                product pricing, enter your merchant GUID below.
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form
                            onSubmit={handleGlobaleSubmit}
                            className="space-y-4"
                        >
                            <div className="space-y-2">
                                <FormLabel htmlFor="globaleGuid">
                                    Merchant GUID
                                </FormLabel>
                                <Input
                                    id="globaleGuid"
                                    name="globaleGuid"
                                    placeholder="Enter your Global-e merchant GUID"
                                    autoComplete="off"
                                />
                                {globaleGuid && (
                                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                                        <Check className="h-4 w-4" />
                                        Current GUID: {maskGuid(globaleGuid)}
                                    </p>
                                )}
                                <p className="text-sm text-muted-foreground">
                                    This identifier is used to connect with your
                                    Global-e account
                                </p>
                            </div>
                            <Button type="submit" disabled={loading}>
                                <LoadingButtonContent loading={loading}>
                                    {globaleGuid ? "Update" : "Save"}
                                </LoadingButtonContent>
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}

export default Integrations;
